import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isEmbedded } from '../utils/embedded';
import './SubscriptionGate.css';
import { isAdminRole } from '../utils/planAccess';

/**
 * SubscriptionGate - Blocks access to features when trial expires
 * Wrap protected content that requires an active subscription
 */
// Map feature names (lowercased) to the minimum plan that unlocks them,
// its price, and its self-serve checkout key. Used by the gate card to render
// a personalized upgrade message ("Upgrade to Serve $59/mo") and deep-link
// straight to that tier's checkout via /pricing?plan=<planKey>. Add new
// entries here when gating a new feature. Keys must match the `feature` prop
// passed to PrivateRoute / SubscriptionGate after `.toLowerCase()`.
const FEATURE_PLAN_MAP = {
  'evergreen webinars': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'custom branding': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'ai tools': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  // Serve and up since Oct 2026 (backend middleware requireTier).
  'ai script generator': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  showrunner: { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'slide decks': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  premieres: { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'promo hub': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'landing pages': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'a/b testing': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'qr codes': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'course builder': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'email automation': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'crm integrations': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'advanced analytics': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'sms notifications': { plan: 'Serve', price: '$59/mo', planKey: 'professional' },
  'api access': { plan: 'Studio', price: '$297/mo', planKey: 'business' },
};

/**
 * Props:
 *   children        — content to render when access is granted
 *   feature         — human-readable feature name used for copy lookups
 *                     in FEATURE_PLAN_MAP below + fallback messaging
 *   blockFreeUsers  — when true, free-plan users are blocked regardless
 *                     of getSubscriptionStatus().hasAccess (which returns
 *                     true for free users). Used by PrivateRoute's
 *                     `requirePaidPlan` flag for Grow hub features that
 *                     should convert free users via upgrade prompt.
 *                     Trial users still pass (they get Professional tier).
 */
const SubscriptionGate = ({ children, feature = 'this feature', blockFreeUsers = false }) => {
  const { currentUser, getSubscriptionStatus } = useAuth();
  const navigate = useNavigate();

  if (!currentUser) {
    return children; // Let PrivateRoute handle auth
  }

  const status = getSubscriptionStatus();

  // Inside the native app shell, Apple Guideline 3.1.1 forbids steering users
  // to an external (Stripe) purchase. So the gate must NOT surface the upgrade
  // CTA (which routes to /pricing → checkout) — show a payment-free,
  // informational message that points to the open web instead.
  const embedded = isEmbedded();

  // Admins always have access
  if (isAdminRole(currentUser.role)) {
    return children;
  }

  // Paid-plan gate: block free users even though hasAccess=true for them.
  // Trial users still pass — trial gives Professional-tier features.
  const isBlockedFree =
    blockFreeUsers && status.isFreePlan && !status.isTrialing;

  // Has access AND not a blocked free user
  if (status.hasAccess && !isBlockedFree) {
    return children;
  }

  // Look up which plan unlocks this feature (name, price, self-serve key)
  const featureLower = feature.toLowerCase();
  const planInfo = FEATURE_PLAN_MAP[featureLower];
  const planName = planInfo?.plan || 'a paid plan';
  const planPrice = planInfo?.price ? ` (${planInfo.price})` : '';
  // Send users to the self-serve /pricing page to pick a tier and see all
  // options (not straight to checkout). The label still names the tier that
  // unlocks this feature so they know where to look.
  const upgradeHref = '/pricing';
  const upgradeLabel = planInfo?.plan ? `Upgrade to ${planInfo.plan}` : 'View plans';

  // Headline + body copy by gate reason. Self-serve upgrade (14-day trial →
  // Stripe checkout on /pricing), NOT the Founding Member application.
  let headline;
  let body;
  if (embedded) {
    // Payment-free copy for the in-app WebView (no upgrade/checkout CTA).
    headline = 'Upgrade on the web to continue';
    body = `${feature} requires an active Canira plan. Open canira.io in your browser to upgrade your account, then come back here.`;
  } else if (isBlockedFree) {
    headline = `${feature} is a ${planName} feature`;
    body = `Upgrade to ${planName}${planPrice} to unlock ${feature}. Start your 14-day free trial — cancel anytime, founding rate locked for life.`;
  } else if (status.trialExpired) {
    headline = 'Your free trial has ended';
    body = `${feature} needs an active plan. Upgrade to ${planName}${planPrice} to pick up right where you left off — founding rate, locked for life.`;
  } else {
    headline = `Upgrade to unlock ${feature}`;
    body = `${feature} is available on ${planName}${planPrice}. Start your 14-day free trial — cancel anytime.`;
  }

  return (
    <div className="subscription-gate">
      <div className="subscription-gate-card">
        <div className="subscription-gate-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="64" height="64">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h2>{headline}</h2>
        <p>{body}</p>
        {!embedded && (
        <div className="subscription-gate-features">
          <h3>What you get:</h3>
          <ul>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <strong>14-day free trial</strong> — no charge for 14 days
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <strong>Founding rate</strong> — locked for life, even after public launch
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <strong>Cancel anytime</strong> — manage it yourself from your account
            </li>
          </ul>
        </div>
        )}
        <div className="subscription-gate-actions">
          {embedded ? (
            <button
              className="gate-btn-secondary"
              onClick={() => {
                // Inside the native shell, ask it to close the WebView; on the
                // open web (or if no bridge) fall back to the dashboard.
                if (window.ReactNativeWebView) {
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'exit' }));
                } else {
                  navigate('/dashboard');
                }
              }}
            >
              Go back
            </button>
          ) : (
            <>
              <button className="gate-btn-primary" onClick={() => navigate(upgradeHref)}>
                {upgradeLabel}
              </button>
              <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGate;
